import { emptySplitApi } from "./emptyApi"
import { TagDto } from "./tagsApi"

// Types for the API responses
export interface MediaTagDto {
  id: string
  itemType: string
  itemId: string
  tag: TagDto
  createdAt: string
}

export interface TagMediaRequestDto {
  itemType: "campaign" | "adset" | "ad" | "post" | "creative"
  itemId: string
  tagId: string
}

export const mediaTagsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    // Get tags for a specific media item
    getMediaTags: builder.query<
      MediaTagDto[],
      { itemType: string; itemId: string }
    >({
      query: ({ itemType, itemId }) => `media-tags/${itemType}/${itemId}`,
      providesTags: (result, _error, { itemType, itemId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "MediaTag" as const, id })),
              { type: "MediaTag", id: "LIST", itemType, itemId },
            ]
          : [{ type: "MediaTag", id: "LIST", itemType, itemId }],
    }),

    // Get available tags for a media item (returns just the tags, not the media tag relationships)
    getAvailableTagsForItem: builder.query<
      TagDto[],
      { itemType: string; itemId: string }
    >({
      query: ({ itemType, itemId }) => `media-tags/${itemType}/${itemId}/tags`,
      providesTags: (result, _error, { itemType, itemId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Tag" as const, id })),
              { type: "Tag", id: "LIST" },
              { type: "MediaTag", id: "LIST", itemType, itemId },
            ]
          : [
              { type: "Tag", id: "LIST" },
              { type: "MediaTag", id: "LIST", itemType, itemId },
            ],
    }),

    // Tag a media item
    tagMediaItem: builder.mutation<MediaTagDto, TagMediaRequestDto>({
      query: data => ({
        url: "media-tags",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { itemType, itemId }) => [
        { type: "MediaTag", id: "LIST", itemType, itemId },
        { type: "Tag", id: "LIST" },
      ],
    }),

    // Remove tag from media item
    removeMediaTag: builder.mutation<void, string>({
      query: mediaTagId => ({
        url: `media-tags/${mediaTagId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, mediaTagId) => [
        { type: "MediaTag", id: mediaTagId },
        { type: "MediaTag", id: "LIST" },
        { type: "Tag", id: "LIST" },
      ],
    }),

    // Bulk tag multiple items
    bulkTagMediaItems: builder.mutation<
      MediaTagDto[],
      { items: Array<{ itemType: string; itemId: string }>; tagId: string }
    >({
      query: ({ items, tagId }) => ({
        url: "media-tags/bulk",
        method: "POST",
        body: { items, tagId },
      }),
      invalidatesTags: (_result, _error, { items }) => [
        ...items.map(({ itemType, itemId }) => ({
          type: "MediaTag" as const,
          id: "LIST",
          itemType,
          itemId,
        })),
        { type: "Tag", id: "LIST" },
      ],
    }),

    // Bulk remove tags from multiple items
    bulkRemoveMediaTags: builder.mutation<
      void,
      { items: Array<{ itemType: string; itemId: string }>; tagId: string }
    >({
      query: ({ items, tagId }) => ({
        url: "media-tags/bulk/remove",
        method: "POST",
        body: { items, tagId },
      }),
      invalidatesTags: (_result, _error, { items }) => [
        ...items.map(({ itemType, itemId }) => ({
          type: "MediaTag" as const,
          id: "LIST",
          itemType,
          itemId,
        })),
        { type: "Tag", id: "LIST" },
      ],
    }),
  }),
})

export const {
  useGetMediaTagsQuery,
  useGetAvailableTagsForItemQuery,
  useTagMediaItemMutation,
  useRemoveMediaTagMutation,
  useBulkTagMediaItemsMutation,
  useBulkRemoveMediaTagsMutation,
} = mediaTagsApi
